/**
 * Frontend Enums matching Laravel Backend Enums
 *
 * IMPORTANT: These must stay in sync with Laravel backend enums:
 * - app/Enums/SaleStatus.php
 * - app/Enums/InvoiceStatus.php
 * - app/Enums/PurchaseStatus.php
 */

// Sale Status Enum (matches app/Enums/SaleStatus.php)
export const SaleStatus = Object.freeze({
  PENDING: 'pending',
  PAID: 'paid',
  PARTIAL: 'partial',
  CANCELLED: 'cancelled',

  // Helper methods
  values: () => [
    SaleStatus.PENDING,
    SaleStatus.PAID,
    SaleStatus.PARTIAL,
    SaleStatus.CANCELLED
  ],

  labels: () => ({
    [SaleStatus.PENDING]: 'Pending',
    [SaleStatus.PAID]: 'Paid',
    [SaleStatus.PARTIAL]: 'Partially Paid',
    [SaleStatus.CANCELLED]: 'Cancelled'
  }),

  colors: () => ({
    [SaleStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
    [SaleStatus.PAID]: 'bg-green-100 text-green-800',
    [SaleStatus.PARTIAL]: 'bg-blue-100 text-blue-800',
    [SaleStatus.CANCELLED]: 'bg-red-100 text-red-800'
  }),

  isValid: (value) => SaleStatus.values().includes(value)
});

// Invoice Status Enum (matches app/Enums/InvoiceStatus.php)
export const InvoiceStatus = Object.freeze({
  DRAFT: 'draft',
  SENT: 'sent',
  PENDING: 'pending',
  PAID: 'paid',
  PARTIAL: 'partial',
  CANCELLED: 'cancelled',

  // Helper methods
  values: () => [
    InvoiceStatus.DRAFT,
    InvoiceStatus.SENT,
    InvoiceStatus.PENDING,
    InvoiceStatus.PAID,
    InvoiceStatus.PARTIAL,
    InvoiceStatus.CANCELLED
  ],

  labels: () => ({
    [InvoiceStatus.DRAFT]: 'Draft',
    [InvoiceStatus.SENT]: 'Sent',
    [InvoiceStatus.PENDING]: 'Pending',
    [InvoiceStatus.PAID]: 'Paid',
    [InvoiceStatus.PARTIAL]: 'Partially Paid',
    [InvoiceStatus.CANCELLED]: 'Cancelled'
  }),

  colors: () => ({
    [InvoiceStatus.DRAFT]: 'bg-gray-100 text-gray-800',
    [InvoiceStatus.SENT]: 'bg-purple-100 text-purple-800',
    [InvoiceStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
    [InvoiceStatus.PAID]: 'bg-green-100 text-green-800',
    [InvoiceStatus.PARTIAL]: 'bg-blue-100 text-blue-800',
    [InvoiceStatus.CANCELLED]: 'bg-red-100 text-red-800'
  }),

  isValid: (value) => InvoiceStatus.values().includes(value)
});

// Purchase Status Enum (matches database ENUM: pending, paid, cancelled)
export const PurchaseStatus = Object.freeze({
  PENDING: 'pending',
  PAID: 'paid',
  CANCELLED: 'cancelled',

  // Helper methods
  values: () => [
    PurchaseStatus.PENDING,
    PurchaseStatus.PAID,
    PurchaseStatus.CANCELLED
  ],

  labels: () => ({
    [PurchaseStatus.PENDING]: 'Pending',
    [PurchaseStatus.PAID]: 'Paid',
    [PurchaseStatus.CANCELLED]: 'Cancelled'
  }),

  colors: () => ({
    [PurchaseStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
    [PurchaseStatus.PAID]: 'bg-green-100 text-green-800',
    [PurchaseStatus.CANCELLED]: 'bg-red-100 text-red-800'
  }),

  isValid: (value) => PurchaseStatus.values().includes(value)
});

// Product Types (existing, kept for compatibility)
export const ProductType = Object.freeze({
  TIRE: 'tire',
  BATTERY: 'battery',
  SERVICE: 'service',
  WIPER: 'wiper',

  values: () => [
    ProductType.TIRE,
    ProductType.BATTERY,
    ProductType.SERVICE,
    ProductType.WIPER
  ],

  labels: () => ({
    [ProductType.TIRE]: 'Tire',
    [ProductType.BATTERY]: 'Battery',
    [ProductType.SERVICE]: 'Service',
    [ProductType.WIPER]: 'Wiper'
  }),

  isValid: (value) => ProductType.values().includes(value)
});

// Payment Methods
export const PaymentMethod = Object.freeze({
  CASH: 'cash',
  CHEQUE: 'cheque',
  CARD: 'card',
  BANK_TRANSFER: 'bank_transfer',

  values: () => [
    PaymentMethod.CASH,
    PaymentMethod.CHEQUE,
    PaymentMethod.CARD,
    PaymentMethod.BANK_TRANSFER
  ],

  labels: () => ({
    [PaymentMethod.CASH]: 'Cash',
    [PaymentMethod.CHEQUE]: 'Cheque',
    [PaymentMethod.CARD]: 'Card',
    [PaymentMethod.BANK_TRANSFER]: 'Bank Transfer'
  }),

  isValid: (value) => PaymentMethod.values().includes(value)
});

// Tire Construction Types
export const TireConstructionType = Object.freeze({
  RADIAL: 'radial',
  BIAS_PLY: 'bias-ply',

  values: () => [
    TireConstructionType.RADIAL,
    TireConstructionType.BIAS_PLY
  ],

  labels: () => ({
    [TireConstructionType.RADIAL]: 'Radial',
    [TireConstructionType.BIAS_PLY]: 'Bias Ply'
  }),

  isValid: (value) => TireConstructionType.values().includes(value)
});

// Tire Ratings
export const TireRating = Object.freeze({
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',

  values: () => ['A', 'B', 'C', 'D', 'E'],

  isValid: (value) => TireRating.values().includes(value)
});

// Battery Types
export const BatteryType = Object.freeze({
  GEL: 'gel',
  ACID: 'acid',
  LITHIUM: 'lithium',

  values: () => [
    BatteryType.GEL,
    BatteryType.ACID,
    BatteryType.LITHIUM
  ],

  labels: () => ({
    [BatteryType.GEL]: 'Gel',
    [BatteryType.ACID]: 'Acid',
    [BatteryType.LITHIUM]: 'Lithium'
  }),

  isValid: (value) => BatteryType.values().includes(value)
});

// Battery Shapes
export const BatteryShape = Object.freeze({
  THIN: 'thin',
  THICK: 'thick',

  values: () => [
    BatteryShape.THIN,
    BatteryShape.THICK
  ],

  labels: () => ({
    [BatteryShape.THIN]: 'Thin',
    [BatteryShape.THICK]: 'Thick'
  }),

  isValid: (value) => BatteryShape.values().includes(value)
});

// Export all as default for convenience
const enums = {
  SaleStatus,
  InvoiceStatus,
  PurchaseStatus,
  ProductType,
  PaymentMethod,
  TireConstructionType,
  TireRating,
  BatteryType,
  BatteryShape
};
export default enums;
